// اجزای مشترک موجودی و گردش AP-INV-02 (SCR-STAFF-871 تا 875، 878، 879): مسیرها، کلیدهای Query، فهرست‌های شمارشی، نمایش مقدار هزارمی به واحد
// پایه یا مصرف (C-AGENT-1201)، انتخاب انبار و کالا.
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import type {
  InvItemUnit,
  InvItemUnitConversionInfo,
  InvStockDocumentStatus,
  InvStockDocumentType,
  InvWarehouseType,
  InvWarehouseView,
  InvWasteReason,
} from '@phoenix/api-client';
import { unwrap } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { faDigits, latinDigits, type StatusTone } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';

export const STOCK_PATH = '/inventory/stock';
export const DOCUMENTS_PATH = '/inventory/documents';
export const OPEN_CONTAINERS_PATH = '/inventory/open-containers';
export const ALERTS_PATH = '/inventory/alerts';
export const STOCKTAKES_PATH = '/inventory/stocktakes';
export const INVENTORY_REPORTS_PATH = '/inventory/reports';
export const INVENTORY_SETTINGS_PATH = '/inventory/settings';
export const INVENTORY_FEATURE = 'feature.inv.inventory';

export const WAREHOUSE_TYPES: readonly InvWarehouseType[] = ['Central', 'Branch', 'RoomCabinet', 'SalesShelf', 'Maintenance'];
export const CREATABLE_DOCUMENTS: readonly InvStockDocumentType[] = ['Receipt', 'Issue', 'Transfer', 'ReturnToSupplier', 'Waste', 'MaintenanceIssue'];
export const DOCUMENT_TYPES: readonly InvStockDocumentType[] = [...CREATABLE_DOCUMENTS, 'OpeningBalance', 'StocktakeAdjustment', 'SalesReturn'];
export const DOCUMENT_STATUSES: readonly InvStockDocumentStatus[] = ['PendingApproval', 'Posted', 'InTransit', 'Received', 'Rejected'];
export const WASTE_REASONS: readonly NonNullable<InvWasteReason>[] = ['Expired', 'Damaged', 'Contaminated', 'OpenedBeyondUse', 'Recalled', 'Other'];
export const REPORT_KINDS = [
  'consumption-by-service',
  'consumption-by-provider',
  'consumption-by-warehouse',
  'bom-variance',
  'top-items',
  'receipts-by-supplier',
  'waste-by-reason',
  'returns-by-supplier',
  'sales-cost',
  'stock-value',
  'turnover',
] as const;
export type ReportKind = (typeof REPORT_KINDS)[number];

export const stockKeys = {
  warehouses: (tenantId: string | undefined) => ['inventory', 'warehouses', tenantId] as const,
  stock: (tenantId: string | undefined, warehouseId: string) => ['inventory', 'stock', tenantId, warehouseId] as const,
  documents: (tenantId: string | undefined) => ['inventory', 'documents', tenantId] as const,
  containers: (tenantId: string | undefined) => ['inventory', 'open-containers', tenantId] as const,
  alerts: (tenantId: string | undefined) => ['inventory', 'alerts', tenantId] as const,
  recalls: (tenantId: string | undefined) => ['inventory', 'recalls', tenantId] as const,
  stocktakes: (tenantId: string | undefined) => ['inventory', 'stocktakes', tenantId] as const,
  rules: (tenantId: string | undefined) => ['inventory', 'reorder-rules', tenantId] as const,
  settings: (tenantId: string | undefined) => ['inventory', 'settings', tenantId] as const,
  salesReturns: (tenantId: string | undefined) => ['inventory', 'sales-returns', tenantId] as const,
};

/** رنگ وضعیت سند انبار. */
export function documentTone(status: InvStockDocumentStatus): StatusTone {
  switch (status) {
    case 'Posted':
    case 'Received':
      return 'done';
    case 'PendingApproval':
    case 'InTransit':
      return 'pending';
    default:
      return 'cancelled';
  }
}

/** هزارم به عدد اعشاری (BR-INV-003: سه رقم اعشار). */
export const fromMilli = (milli: number | string | null | undefined): number => Math.round(Number(milli ?? 0)) / 1000;

/** عدد کاربر (ارقام فارسی یا لاتین، اعشار با / یا .) به هزارم؛ نامعتبر ← null. */
export function toMilli(text: string): number | null {
  const normalized = latinDigits(text.trim())
    .replace(/[\u066B/]/g, '.')
    .replace(/[,\u066C]/g, '');
  if (!/^-?[0-9]+(\.[0-9]{1,3})?$/.test(normalized)) {
    return null;
  }
  return Math.round(Number(normalized) * 1000);
}

/** مقدار هزارمی به واحد داده‌شده (با تبدیل کالا) برای نمایش. */
export function milliIn(milliOfBase: number | string, unit: InvItemUnit, baseUnit: InvItemUnit, conversions: readonly InvItemUnitConversionInfo[]): number {
  if (unit === baseUnit) {
    return fromMilli(milliOfBase);
  }
  const conversion = conversions.find((candidate) => candidate.unit === unit);
  if (!conversion) {
    return fromMilli(milliOfBase);
  }
  return (fromMilli(milliOfBase) * Number(conversion.unitQuantity)) / Number(conversion.baseQuantity);
}

/** متن مقدار با واحد: «۲٫۶ ویال». */
export function useQuantityText() {
  const { t } = useTranslation('inv');
  return (milliOfBase: number | string | null | undefined, unit: InvItemUnit) =>
    `${faDigits(Number(fromMilli(milliOfBase).toFixed(3)).toString())} ${t(`inv:unit.${unit}`)}`;
}

/** انبارهای مستأجر. */
export function useWarehouses() {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: stockKeys.warehouses(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/warehouses')),
  });
}

/** کالاهای فعال برای انتخاب در اسناد. */
export function useItemOptions() {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: ['inventory', 'items', session?.tenantId, 'options'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/items', { params: { query: { active: true, limit: 100 } } })),
  });
}

/** نام انبار از فهرست. */
export function warehouseName(warehouses: readonly InvWarehouseView[] | undefined, id: string | null | undefined): string {
  return warehouses?.find((warehouse) => warehouse.id === id)?.name ?? '—';
}

/** انتخاب انبار (کنترل‌شده). */
export function WarehouseSelect({
  value,
  onChange,
  labelKey = 'inv:stock.warehouse',
  testId,
  includeEmpty = false,
  required = false,
}: {
  value: string;
  onChange: (id: string) => void;
  labelKey?: string;
  testId?: string;
  includeEmpty?: boolean;
  required?: boolean;
}) {
  const { t } = useTranslation('inv');
  const warehouses = useWarehouses();
  return (
    <TextField
      select
      size="small"
      required={required}
      label={t(labelKey)}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      sx={{ minWidth: 240 }}
      slotProps={{ htmlInput: { 'data-testid': testId } }}
    >
      {includeEmpty && <MenuItem value="">{t('inv:items.filters.all')}</MenuItem>}
      {(warehouses.data?.items ?? [])
        .filter((warehouse) => warehouse.isActive)
        .map((warehouse) => (
          <MenuItem key={warehouse.id} value={warehouse.id}>
            {warehouse.name} · {t(`inv:warehouse_type.${warehouse.type}`)}
          </MenuItem>
        ))}
    </TextField>
  );
}
